/* Aesthesy variant selection — resolved in the browser, no server round trip.

   Shared by the PDP and the quick view. Both embed their whole variant table,
   so choosing an option is a lookup: update the hidden id, the price, the stock
   badge, the button and the gallery. Nothing is refetched and no DOM is
   swapped, which is the difference from Be Yours' <product-info>.

   Why not <product-info>: it answers every option click by refetching the
   section and swapping markup. On this theme that broke three ways — the
   Section Rendering API drops section blocks, the form id has to match a
   hardcoded selector, and the swapped-in picker needed re-decorating. None of
   that can happen here.

   Markup contract, on the root element:
     data-aes-variants     JSON: [{id, options[], available, price, compare,
                                   off, media}]
     data-aes-buy          button label when purchasable
     data-aes-soldout      label when the variant exists but is out of stock
     data-aes-unavailable  label when the chosen combination does not exist
                           (a LABEL, not a flag — the per-option marker is
                           data-aes-oos, deliberately a different name: sharing
                           one name meant the CSS matched this root element and
                           faded the entire page)
     data-aes-url          product url; when present the address bar gets
                           ?variant= (PDP only — a dialog must not do this)
   Inside it:
     form[action*="/cart/add"] containing input[name="id"]
     fieldset per option, each with the option's inputs
     [data-aes-now] [data-aes-was] [data-aes-off]   price, optional
     [data-aes-badge]                               stock badge, optional
     [data-aes-scroller]                            gallery strip, optional */
(() => {
  if (window.__aesVariantsBooted) return;
  window.__aesVariantsBooted = true;

  const bind = (root) => {
    if (!root || root.dataset.aesVarBound) return;
    let variants = [];
    try { variants = JSON.parse(root.dataset.aesVariants || '[]'); } catch (_) { return; }
    if (!variants.length) return;

    const form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    root.dataset.aesVarBound = '1';

    const idInput = form.querySelector('input[name="id"]');
    const submit = form.querySelector('[type="submit"]');
    const submitText = submit?.querySelector('span') || submit;
    // Only the LIVE picker's fieldsets. Be Yours renders <variant-selects> more
    // than once (a primary plus an alternate), so a blind querySelectorAll here
    // returned six groups for a three-option product: every lookup indexed past
    // the end of variant.options, nothing matched, and the whole picker was
    // marked unavailable — the greyed-out page.
    const arity = variants[0].options.length;
    const picker =
      root.querySelector('variant-selects[data-primary]') ||
      [...root.querySelectorAll('variant-selects')].find((vs) => vs.style.display !== 'none') ||
      root;
    const groups = [...picker.querySelectorAll('fieldset')].slice(0, arity);

    // Labels come from the markup, never from what the button currently says:
    // reading it live means a product whose first variant is sold out captures
    // "Sold out" as its buy label and never recovers.
    const BUY = root.dataset.aesBuy || '';
    const SOLD = root.dataset.aesSoldout || 'Sold out';
    const GONE = root.dataset.aesUnavailable || 'Unavailable';

    const now = root.querySelector('[data-aes-now]');
    const was = root.querySelector('[data-aes-was]');
    const off = root.querySelector('[data-aes-off]');
    // The PDP wraps MRP and the discount badge together, so the wrapper is what
    // hides — hiding the <s> alone would leave a stray "MRP" label behind.
    const compare = root.querySelector('[data-aes-compare]');
    const badge = root.querySelector('[data-aes-badge]');
    const scroller = root.querySelector('[data-aes-scroller]');

    const chosen = () => groups.map((fs) => fs.querySelector('input:checked')?.value ?? null);

    const setButton = (enabled, label) => {
      if (!submit) return;
      submit.disabled = !enabled;
      submit.setAttribute('aria-disabled', String(!enabled));
      if (submitText && label) submitText.textContent = label;
    };

    // Flag option values that cannot lead to a sellable variant given the other
    // current choices. Without this a shopper picks a size, then taps a frame
    // that does not exist for it and nothing happens — a dead click.
    const markOptions = (picked) => {
      // Fail safe: if the groups do not line up with the option arity, mark
      // nothing rather than fading everything.
      if (groups.length !== arity) return;
      groups.forEach((fs, i) => {
        fs.querySelectorAll('input').forEach((input) => {
          const others = (x) => picked.every((val, j) => j === i || val == null || x.options[j] === val);
          const sellable = variants.some((x) => x.options[i] === input.value && others(x) && x.available);
          // Only ever a LABEL. The old `|| input.parentElement` fallback was
          // the bug behind the greyed-out PDP: the size pills render the input
          // as a SIBLING of its label, so closest('label') was null and the
          // parent — the whole option group — got faded instead. Three groups
          // later the entire page looked disabled. Fail safe: if no precise
          // label is found, mark nothing.
          const target =
            input.closest('.swatch-input__label, label') ||
            (input.id ? fs.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null);
          if (target) target.toggleAttribute('data-aes-oos', !sellable);
          // keep it focusable for screen readers; the styling conveys the state
          input.setAttribute('aria-disabled', String(!sellable));
        });
      });
    };

    const sync = (userChange) => {
      const picked = chosen();
      markOptions(picked);
      if (picked.some((v) => v == null)) return;

      if (picked.length !== arity) return;
      const v = variants.find((x) => picked.every((val, i) => x.options[i] === val));
      if (!v) {
        if (idInput) idInput.value = '';
        setButton(false, GONE);
        return;
      }

      if (idInput) idInput.value = v.id;
      if (now) now.textContent = v.price;
      // 0 is not a discount. A compare-at price a rupee above the price still
      // rounds to 0%, which rendered "0% OFF".
      const hasOff = v.off != null && v.off > 0;
      if (compare) compare.hidden = !hasOff;
      if (was) { was.hidden = !v.compare; if (v.compare) was.textContent = v.compare; }
      if (off) { off.hidden = !hasOff; if (hasOff) off.textContent = v.off + '% OFF'; }
      if (badge) { badge.hidden = !v.badge; if (v.badge) badge.textContent = v.badge; }
      setButton(v.available, v.available ? BUY : SOLD);

      // Gallery: scroll to the variant's own image when it has one.
      if (scroller && typeof v.media === 'number' && v.media >= 0) {
        const slide = scroller.children[v.media];
        if (slide) scroller.scrollTo({ left: slide.offsetLeft - scroller.offsetLeft, behavior: 'smooth' });
      }

      // Address bar, PDP only. A dialog over a collection must not rewrite it,
      // so the quick view simply omits data-aes-url.
      //
      // ONLY on a shopper's change, and ONLY the variant param. The initial
      // sync used to rewrite the URL too, and it ran before Shopify's pixel
      // fired page_viewed: every utm_* / fbclid on an ad landing was gone by
      // the time the visit was recorded, so paid Meta sessions showed up
      // unattributed for two days after go-live.
      if (userChange && root.dataset.aesUrl) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u.toString());
      }
    };

    root.addEventListener('change', (e) => { if (e.target.matches('input')) sync(true); });
    sync(false);
  };

  const boot = () => document.querySelectorAll('[data-aes-variants]').forEach(bind);
  boot();
  document.addEventListener('aes:content-added', boot);
  document.addEventListener('shopify:section:load', boot);
})();
